<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, url } from '@vuelidate/validators';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Р9.3: добавление материалов в document-базу — файлы (несколько за раз,
// drag-n-drop), внешняя ссылка или текст в Markdown.
const props = defineProps({
  knowledgeBaseId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'createSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const fileInputRef = ref(null);

const i18nKey = 'ASSISTANT.KNOWLEDGE_BASES.ADD_DOCUMENT';

const uiFlags = useMapGetter('assistantDocuments/getUIFlags');
const isLoading = computed(() => uiFlags.value.creatingItem);

const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'txt', 'md', 'markdown'];

const TAB_KEYS = ['files', 'link', 'text'];
const activeTabIndex = ref(0);
const activeTab = computed(() => TAB_KEYS[activeTabIndex.value]);

const tabs = computed(() =>
  TAB_KEYS.map(key => ({
    key,
    label: t(`${i18nKey}.TABS.${key.toUpperCase()}`),
  }))
);

const state = reactive({
  files: [],
  name: '',
  url: '',
  content: '',
});

const isDragging = ref(false);
const filesError = ref('');

const validationRules = {
  url: {
    required: requiredIf(() => activeTab.value === 'link'),
    url,
  },
  content: {
    required: requiredIf(() => activeTab.value === 'text'),
  },
};

const v$ = useVuelidate(validationRules, state);

const formErrors = computed(() => ({
  url: v$.value.url.$error ? t('ASSISTANT.DOCUMENTS.FORM.URL.ERROR') : '',
  content: v$.value.content.$error
    ? t('ASSISTANT.KNOWLEDGE_BASES.EDIT_DOCUMENT.CONTENT.ERROR')
    : '',
}));

const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.key === tab.key);
  filesError.value = '';
  v$.value.$reset();
};

const isAllowedFile = file =>
  ALLOWED_EXTENSIONS.includes(file.name.split('.').pop()?.toLowerCase());

const addFiles = fileList => {
  const incoming = Array.from(fileList || []).filter(isAllowedFile);
  // Не дублируем уже выбранные файлы (по имени и размеру).
  incoming.forEach(file => {
    const exists = state.files.some(
      existing => existing.name === file.name && existing.size === file.size
    );
    if (!exists) state.files.push(file);
  });
  if (state.files.length) filesError.value = '';
};

const onFilesPicked = event => {
  addFiles(event.target.files);
  event.target.value = '';
};

const onDrop = event => {
  isDragging.value = false;
  addFiles(event.dataTransfer?.files);
};

const removeFile = index => {
  state.files.splice(index, 1);
};

const openFilePicker = () => fileInputRef.value?.click();

const uploadFiles = () => {
  const formData = new FormData();
  formData.append('knowledge_base_id', props.knowledgeBaseId);
  state.files.forEach(file => formData.append('files[]', file));
  return store.dispatch('assistantDocuments/upload', formData);
};

const createDocument = () => {
  const details = {
    knowledge_base_id: props.knowledgeBaseId,
    name: state.name || state.url,
  };
  if (activeTab.value === 'link') {
    details.external_link = state.url;
  } else {
    details.content = state.content;
  }
  return store.dispatch('assistantDocuments/create', details);
};

const handleSubmit = async () => {
  if (activeTab.value === 'files') {
    if (!state.files.length) {
      filesError.value = t(`${i18nKey}.FILE_ERROR`);
      return;
    }
  } else {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;
  }

  try {
    if (activeTab.value === 'files') {
      await uploadFiles();
    } else {
      await createDocument();
    }
    emit('createSuccess');
    useAlert(t(`${i18nKey}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    // Стор пробрасывает Error с текстом ответа сервера (например, 422 про
    // qa-базу) — показываем его, а не общий текст.
    const errorMessage =
      error?.message ||
      parseAPIErrorResponse(error) ||
      t(`${i18nKey}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => emit('close');
const handleCancel = () => dialogRef.value.close();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t(`${i18nKey}.DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        class="w-full [&>ul]:w-full"
        @tab-changed="handleTabChange"
      />

      <template v-if="activeTab === 'files'">
        <div
          class="flex flex-col items-center justify-center gap-1 px-4 py-8 text-center border-2 border-dashed rounded-xl cursor-pointer transition-colors"
          :class="
            isDragging
              ? 'border-n-blue-9 bg-n-blue-2'
              : 'border-n-weak hover:border-n-slate-8'
          "
          @click="openFilePicker"
          @drop.prevent="onDrop"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
        >
          <Icon icon="i-lucide-upload" class="size-5 text-n-slate-11" />
          <span class="text-sm text-n-slate-12">
            {{ $t(`${i18nKey}.DROPZONE.HINT`) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t(`${i18nKey}.DROPZONE.FORMATS`) }}
          </span>
          <input
            ref="fileInputRef"
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.md"
            class="hidden"
            @change="onFilesPicked"
          />
        </div>

        <div v-if="state.files.length" class="flex flex-col gap-1.5">
          <span class="text-xs text-n-slate-11">
            {{
              $t(`${i18nKey}.DROPZONE.SELECTED`, { count: state.files.length })
            }}
          </span>
          <div
            v-for="(file, index) in state.files"
            :key="`${file.name}-${file.size}`"
            class="flex items-center justify-between gap-2 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12"
          >
            <span class="truncate">{{ file.name }}</span>
            <Button
              icon="i-lucide-x"
              color="slate"
              size="xs"
              variant="ghost"
              class="shrink-0"
              @click="removeFile(index)"
            />
          </div>
        </div>
        <span v-if="filesError" class="text-xs text-n-ruby-11">
          {{ filesError }}
        </span>
      </template>

      <template v-else-if="activeTab === 'link'">
        <Input
          v-model="state.url"
          :label="$t('ASSISTANT.DOCUMENTS.FORM.URL.LABEL')"
          :placeholder="$t('ASSISTANT.DOCUMENTS.FORM.URL.PLACEHOLDER')"
          :message="formErrors.url"
          :message-type="formErrors.url ? 'error' : 'info'"
        />
        <Input
          v-model="state.name"
          :label="$t('ASSISTANT.DOCUMENTS.FORM.NAME.LABEL')"
          :placeholder="$t('ASSISTANT.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
        />
      </template>

      <template v-else>
        <Input
          v-model="state.name"
          :label="$t('ASSISTANT.DOCUMENTS.FORM.NAME.LABEL')"
          :placeholder="$t('ASSISTANT.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
        />
        <TextArea
          v-model="state.content"
          :label="$t('ASSISTANT.KNOWLEDGE_BASES.EDIT_DOCUMENT.CONTENT.LABEL')"
          :placeholder="
            $t('ASSISTANT.KNOWLEDGE_BASES.EDIT_DOCUMENT.CONTENT.PLACEHOLDER')
          "
          :max-length="100000"
          auto-height
          :message="formErrors.content"
          :message-type="formErrors.content ? 'error' : 'info'"
        />
      </template>

      <div class="flex gap-3 justify-between items-center w-full">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="$t('ASSISTANT.FORM.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
          @click="handleCancel"
        />
        <Button
          type="button"
          :label="$t(`${i18nKey}.SUBMIT`)"
          class="w-full"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="handleSubmit"
        />
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
